import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { ProgressSpinner } from "primereact/progressspinner";
import useFetch from "../../hooks/use-fetch";
import Config from "../../config";
import { useParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { DateTime } from 'luxon-business-days';
import { Panel } from "primereact/panel";
import DateRangePicker from "../../date-pickers/date-range-picker";
import { RadioButton, RadioButtonChangeEvent } from "primereact/radiobutton";
import { TreeTable } from "primereact/treetable";
import { Accordion, AccordionTab } from "primereact/accordion";
import { Button } from 'primereact/button';
import { EChartsOption } from "echarts";
import { ReactECharts } from "../../echarts/react-echarts";
import RejectionsPerStationPerPersonFrameChartOptions from "./rejections-per-station-per-person";
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

interface RejectionComment {
      comment: string;
      count: number;
}
interface RejectionReason {
      reason: string;
      count: number;
      comments: RejectionComment[];
}

interface RejectionEntry {
      station: string;
      count: number;
      reasons: RejectionReason[]
}

interface IRejectionAtStation {
      date: string;
      station: string;
      stationId: string;
      userName: string;
      userId: string,
      rejections: number,
      totalRejections: number
}

interface ProductionVersusRejectsAtStation {
      date: string;
      station: string;
      stationId: string;
      userName: string;
      userId: string;
      rejections: number;
      totalRejections: number;
      totalFrames: number;
}

interface IRejectionReportResult {
      rejectionsPerUserPerStation: IRejectionAtStation[],
      rejectionByType: RejectionEntry[]
      rejectionsVersusCompleted: IRejectionsVersusCompletedResult[]
}

interface IRejectionsVersusCompletedResult {
      date: string;
      stationName: string;
      stationId: string;
      rejections: number,
      completed: number,
      rejectionRate: number,
      //  totalRejections: number
}
interface Totals {
      completed: number;
      rejections: number;
      rate: string;
}

interface StationTotals {
      [stationName: string]: Totals;
}

const RejectionsReport = () => {

      const [stationCharts, setStationCharts] = useState([] as any);

      const [startDate, setStartDate] = useState<DateTime | null>(DateTime.now().startOf('month'));
      const [endDate, setEndDate] = useState<DateTime | null>(DateTime.now());

      const [range, setRange] = useState<"day" | "weekly" | "month" | "quarter">("day");

      const [url, setUrl] = useState<string | null>('');

      const [showListView, setShowListVIew] = useState<Boolean>(false);

      const rangeOptions: string[] = ['day', "week", 'month', "quarter"];

      const { data, loading, error } = useFetch<IRejectionReportResult>(url ?? "", { rejectionByType: [], rejectionsPerUserPerStation: [], rejectionsVersusCompleted: [] } as IRejectionReportResult, {});

      const [eBarChartOptions, setEBarChartOptions] = useState<EChartsOption>()


      const [rawData, setRawData] = useState<IRejectionsVersusCompletedResult[]>([]);
      const [dataByDate, setDataByDate] = useState<Record<string, IRejectionsVersusCompletedResult[]>>({});
      const [totals, setTotals] = useState<Totals>({ completed: 0, rejections: 0, rate: '0.00' });
      const [stationTotals, setStationTotals] = useState<StationTotals>({});

      const [availableDates, setAvailableDates] = useState<string[]>([]);
      const [availableStations, setAvailableStations] = useState<string[]>([]);
      const [selectedDate, setSelectedDate] = useState<string>('All');
      const [selectedStation, setSelectedStation] = useState<string>('All');

      useEffect(() => {
            const url2 = range && startDate && endDate && `${Config.baseUrl}/api/admin/reports/production/rejections?startdate=${startDate.toFormat('yyyy-MM-dd')
                  + 'T00:00:01'}&enddate=${endDate.toFormat('yyyy-MM-dd') + 'T23:59:59'}&range=${range}`;
            setUrl(url2);
      }, []);

      useEffect(() => {

            const url2 = range && startDate && endDate && `${Config.baseUrl}/api/admin/reports/production/rejections?startdate=${startDate.toFormat('yyyy-MM-dd')
                  + 'T00:00:01'}&enddate=${endDate.toFormat('yyyy-MM-dd') + 'T23:59:59'}&range=${range}`;
            setUrl(url2!);

      }, [startDate, endDate]);

      useEffect(() => {

            const buildChartOption = () => {
                  const stations = [...new Set(data.rejectionsPerUserPerStation.map(d => d.station))];
                  const users = [...new Set(data.rejectionsPerUserPerStation.map(d => d.userName))];

                  // Initialize series for each user
                  const series = users.map(user => {
                        const userData = stations.map(station => {
                              const total = data.rejectionsPerUserPerStation
                                    .filter(d => d.station === station && d.userName === user)
                                    .reduce((sum, item) => sum + item.rejections, 0);
                              return total;
                        });
                        return {
                              name: user,
                              type: 'bar',
                              stack: 'total',
                              label: {
                                    show: true,
                                    position: 'inside',
                                    formatter: (params: { value: number; }) => {
                                          return params.value > 0 ? `${user} (${params.value})` : '';
                                    },
                                    fontSize: 12,
                                    color: '#fff'
                              },
                              data: userData
                        };
                  });

                  let chartOptions = {
                        title: {
                              text: 'Total Rejections per User per Station',
                              left: 'center'
                        },
                        tooltip: {
                              trigger: 'axis',
                              axisPointer: { type: 'shadow' }
                        },
                        legend: {
                              top: 30
                        },
                        grid: {
                              left: '3%',
                              right: '4%',
                              bottom: '10%',
                              containLabel: true
                        },
                        xAxis: {
                              type: 'category',
                              data: stations
                        },
                        yAxis: {
                              type: 'value'
                        },
                        series
                  } as EChartsOption;

                  setEBarChartOptions(chartOptions);

            }




            if (data && data.rejectionsPerUserPerStation && data.rejectionsPerUserPerStation.length > 0) {
                  buildChartOption()

                  const processed = data.rejectionsVersusCompleted.map(entry => {
                        const completed = entry.completed || 0;
                        const rejections = entry.rejections || 0;
                        const rate = completed + rejections > 0
                              ? ((rejections / (completed + rejections)) * 100).toFixed(2)
                              : '0.00';
                        return { ...entry, rejectionRate: rate };
                  });

                  setRawData(processed);

                  // Populate filters
                  const dates = Array.from(new Set(processed.map(r => r.date))).sort();
                  const stations = Array.from(new Set(processed.map(r => r.stationName))).sort();
                  setAvailableDates(dates);
                  setAvailableStations(stations);

            }

      }, [data]);

      const filteredData = useMemo(() => {
            return rawData.filter(record => {
                  const dateMatch = selectedDate === 'All' || record.date === selectedDate;
                  const stationMatch = selectedStation === 'All' || record.stationName === selectedStation;
                  return dateMatch && stationMatch;
            });
      }, [rawData, selectedDate, selectedStation]);

      const exportToExcel = (): void => {
            if (filteredData.length === 0) return;

            // Sheet 1: Raw filtered data
            const dataToExport = filteredData.map((row): Record<string, string | number> => ({
                  Date: row.date,
                  Station: row.stationName,
                  Completed: row.completed,
                  Rejected: row.rejections,
                  'Rejection Rate (%)': row.completed > 0
                        ? ((row.rejections / row.completed) * 100).toFixed(2)
                        : '0.00',
            }));

            const summarySheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(dataToExport);

            // Sheet 2: Per-station and overall totals
            const stationTotalsMap: Record<string, { completed: number; rejections: number }> = {};

            filteredData.forEach((row) => {
                  if (!stationTotalsMap[row.stationName]) {
                        stationTotalsMap[row.stationName] = { completed: 0, rejections: 0 };
                  }
                  stationTotalsMap[row.stationName].completed += row.completed;
                  stationTotalsMap[row.stationName].rejections += row.rejections;
            });

            const stationTotals: Array<Record<string, string | number>> = Object.entries(stationTotalsMap).map(
                  ([station, totals]) => ({
                        Station: station,
                        Completed: totals.completed,
                        Rejected: totals.rejections,
                        'Rejection Rate (%)':
                              totals.completed > 0
                                    ? ((totals.rejections / totals.completed) * 100).toFixed(2)
                                    : '0.00',
                  })
            );

            // Grand total for all stations
            const totalCompleted = filteredData.reduce((sum, r) => sum + r.completed, 0);
            const totalRejections = filteredData.reduce((sum, r) => sum + r.rejections, 0);
            const overallRejectionRate =
                  totalCompleted > 0 ? ((totalRejections / totalCompleted) * 100).toFixed(2) : '0.00';

            stationTotals.push({
                  Station: 'Grand Total',
                  Completed: totalCompleted,
                  Rejected: totalRejections,
                  'Rejection Rate (%)': overallRejectionRate,
            });

            const totalsSheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(stationTotals);

            // Create workbook
            const workbook: XLSX.WorkBook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, summarySheet, 'Rejection Summary');
            XLSX.utils.book_append_sheet(workbook, totalsSheet, 'Station Totals');

            const excelBuffer: ArrayBuffer = XLSX.write(workbook, {
                  bookType: 'xlsx',
                  type: 'array',
            });

            const blob: Blob = new Blob([excelBuffer], {
                  type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });

            saveAs(blob, `RejectionSummary_${new Date().toISOString().slice(0, 10)}.xlsx`);
      };

      // Group by date
      useEffect(() => {
            const grouped: Record<string, IRejectionsVersusCompletedResult[]> = {};
            filteredData.forEach(record => {
                  if (!grouped[record.date]) {
                        grouped[record.date] = [];
                  }
                  grouped[record.date].push(record);
            });
            setDataByDate(grouped);

            // Grand total (all filtered data)
            const totalCompleted = filteredData.reduce((sum, r) => sum + r.completed, 0);
            const totalRejections = filteredData.reduce((sum, r) => sum + r.rejections, 0);
            const totalRate = totalCompleted + totalRejections > 0
                  ? ((totalRejections / (totalCompleted + totalRejections)) * 100).toFixed(2)
                  : '0.00';
            setTotals({ completed: totalCompleted, rejections: totalRejections, rate: totalRate });

            // Totals per station
            const stationTotalMap: StationTotals = {};
            filteredData.forEach(({ stationName, completed, rejections }) => {
                  if (!stationTotalMap[stationName]) {
                        stationTotalMap[stationName] = { completed: 0, rejections: 0, rate: '0.00' };
                  }
                  stationTotalMap[stationName].completed += completed;
                  stationTotalMap[stationName].rejections += rejections;
            });

            Object.keys(stationTotalMap).forEach(station => {
                  const c = stationTotalMap[station].completed;
                  const r = stationTotalMap[station].rejections;
                  stationTotalMap[station].rate = c + r > 0 ? ((r / (c + r)) * 100).toFixed(2) : '0.00';
            });

            setStationTotals(stationTotalMap);
      }, [filteredData]);


      const thStyle: React.CSSProperties = {
            padding: '10px',
            borderBottom: '2px solid #ccc',
            textAlign: 'left',
            fontWeight: 'bold'
      };

      const tdStyle: React.CSSProperties = {
            padding: '8px',
            textAlign: 'left'
      };


      return (
            <div className='report' >

                  <div className="calendar-selector">
                        <div className='flex flex-row pl-5'>

                              <DateRangePicker startDateCallback={(dateString) => {
                                    setStartDate(DateTime.fromISO(dateString));
                              }}
                                    endDateCallback={(dateString) => {
                                          setEndDate(DateTime.fromISO(dateString));
                                    }}
                              />

                        </div>
                  </div>


                  <Panel className={"body-panel"}>
                        <div className="report-title-wrapper">
                              <div className="report-title">Rejections Report</div>
                              <div className="report-date-title">{startDate && startDate.toFormat('MMM dd yyyy')} to {endDate && endDate.toFormat('MMM dd yyyy')} </div>
                        </div>

                        {error && <div>An error occurred loading the report.</div>}

                        {loading && <div> <ProgressSpinner /> Loading...</div>}

                        {!(data && data.rejectionByType && data.rejectionsPerUserPerStation && data.rejectionByType.length && data.rejectionsPerUserPerStation.length && data.rejectionByType.length > 0 && data.rejectionsPerUserPerStation.length > 0) &&
                              <div>No rejections were found during this period.</div>
                        }

                        {(data && data.rejectionByType && data.rejectionsPerUserPerStation && data.rejectionByType.length && data.rejectionsPerUserPerStation.length && data.rejectionByType.length > 0 && data.rejectionsPerUserPerStation.length > 0) &&
                              <>
                                    <Accordion multiple activeIndex={[0]}>
                                          {data && data.rejectionByType.map(rejectionEntry => {
                                                return (
                                                      <AccordionTab header={<div><span className="text-lg">{rejectionEntry.station} </span> <span className="text-gray-500">(Total Rejections:{(rejectionEntry.count)})</span></div>}>

                                                            <p className="m-0">
                                                                  <Accordion multiple activeIndex={[0]}>
                                                                        {rejectionEntry.reasons && rejectionEntry.reasons.map(reasons => {
                                                                              return (
                                                                                    <AccordionTab header={<div>{reasons.reason} ({reasons.count})</div>}>

                                                                                          <ol>

                                                                                                {reasons.comments && reasons.comments.map(comments => {
                                                                                                      return (
                                                                                                            <li className="pb-1">{comments.comment} ({comments.count})</li>
                                                                                                      )
                                                                                                })
                                                                                                }
                                                                                          </ol>

                                                                                    </AccordionTab>
                                                                              )
                                                                        })}
                                                                  </Accordion>
                                                            </p>

                                                      </AccordionTab>
                                                )
                                          })}
                                    </Accordion>
                              </>}

                  </Panel>
                  <Panel className={"body-panel mt-2"}>

                        <Button label="Show copiable list" onClick={() => { console.log(showListView); setShowListVIew(!showListView); }} />

                        {showListView === true && data.rejectionByType && data.rejectionByType.map(rejectionEntry => {
                              return (
                                    <div>
                                          <br />
                                          <div className="font-bold text-xl pt-2">{rejectionEntry.station}</div>
                                          {rejectionEntry.reasons && rejectionEntry.reasons.map(reasons => {
                                                return (
                                                      <>
                                                            <div className="text-lg font-medium pl-2" style={{ whiteSpace: "pre-wrap" }}> Reason: {reasons.reason} ({reasons.count})</div>
                                                            &ensp;{reasons.comments && reasons.comments.map(comments => {
                                                                  return (
                                                                        <div className="pl-4">{comments.comment} ({comments.count})</div>
                                                                  )
                                                            })
                                                            }
                                                            <br />
                                                      </>
                                                )
                                          })}

                                    </div>
                              )
                        })}
                  </Panel>
                  {(data && data.rejectionByType && data.rejectionsPerUserPerStation && data.rejectionByType.length && data.rejectionsPerUserPerStation.length && data.rejectionByType.length > 0 && data.rejectionsPerUserPerStation.length > 0) &&
                        <>
                              <Panel className={"body-panel mt-2"}>
                                    {eBarChartOptions &&
                                          <>
                                                <div style={{ width: "100%", minHeight: "500px" }}>
                                                      <ReactECharts option={eBarChartOptions} style={{ height: '1800px', width: "100%" }} />
                                                </div>
                                          </>

                                    }

                              </Panel>

                              <Panel >
                                    <div style={{ padding: '20px', maxWidth: '1000px', margin: '0 auto' }}>
                                          <h2>Station Production & Rejection Summary</h2>

                                          {/* Filters */}
                                          <div style={{ marginBottom: '20px' }}>
                                                <label style={{ marginRight: '20px' }}>
                                                      Filter by Date:
                                                      <select value={selectedDate} onChange={e => setSelectedDate(e.target.value)} style={{ marginLeft: '8px' }}>
                                                            <option value="All">All</option>
                                                            {availableDates.map(date => (
                                                                  <option key={date} value={date}>{date}</option>
                                                            ))}
                                                      </select>
                                                </label>

                                                <label>
                                                      Filter by Station:
                                                      <select value={selectedStation} onChange={e => setSelectedStation(e.target.value)} style={{ marginLeft: '8px' }}>
                                                            <option value="All">All</option>
                                                            {availableStations.map(station => (
                                                                  <option key={station} value={station}>{station}</option>
                                                            ))}
                                                      </select>
                                                </label>
                                          </div>
                                          <button onClick={exportToExcel} style={{ padding: '6px 12px', marginLeft: '10px' }}>
                                                Export to Excel
                                          </button>
                                          {/* Grouped by date */}
                                          {Object.entries(dataByDate).map(([date, records]) => (
                                                <div key={date} style={{ marginBottom: '30px' }}>
                                                      <h3 style={{ borderBottom: '2px solid #ddd' }}>{date}</h3>
                                                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                                            <thead>
                                                                  <tr style={{ backgroundColor: '#f9f9f9' }}>
                                                                        <th style={thStyle}>Station</th>
                                                                        <th style={thStyle}>Completed</th>
                                                                        <th style={thStyle}>Rejected</th>
                                                                        <th style={thStyle}>Rejection Rate (%)</th>
                                                                  </tr>
                                                            </thead>
                                                            <tbody>
                                                                  {records.map((row, idx) => (
                                                                        <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                                                                              <td style={tdStyle}>{row.stationName}</td>
                                                                              <td style={tdStyle}>{row.completed}</td>
                                                                              <td style={tdStyle}>{row.rejections}</td>
                                                                              <td style={tdStyle}>{row.rejectionRate}</td>
                                                                        </tr>
                                                                  ))}
                                                            </tbody>
                                                      </table>
                                                </div>
                                          ))}



                                          {/* Totals per station */}
                                          <h3 style={{ borderTop: '2px solid #aaa', paddingTop: '10px' }}>Grand Total Per Station</h3>
                                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                                <thead>
                                                      <tr style={{ backgroundColor: '#f9f9f9' }}>
                                                            <th style={thStyle}>Station</th>
                                                            <th style={thStyle}>Completed</th>
                                                            <th style={thStyle}>Rejected</th>
                                                            <th style={thStyle}>Rejection Rate (%)</th>
                                                      </tr>
                                                </thead>
                                                <tbody>
                                                      {Object.entries(stationTotals).map(([station, stats]) => (
                                                            <tr key={station} style={{ borderBottom: '1px solid #eee' }}>
                                                                  <td style={tdStyle}>{station}</td>
                                                                  <td style={tdStyle}>{stats.completed}</td>
                                                                  <td style={tdStyle}>{stats.rejections}</td>
                                                                  <td style={tdStyle}>{stats.rate}</td>
                                                            </tr>
                                                      ))}
                                                </tbody>
                                          </table>
                                    </div>
                                    {/* Grand total */}
                                    <h3 style={{ borderTop: '2px solid #aaa', paddingTop: '10px' }}>Grand Total (All Stations)</h3>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '30px' }}>
                                          <thead>
                                                <tr style={{ backgroundColor: '#f0f0f0' }}>
                                                      <th style={thStyle}>Completed</th>
                                                      <th style={thStyle}>Rejected</th>
                                                      <th style={thStyle}>Rejection Rate (%)</th>
                                                </tr>
                                          </thead>
                                          <tbody>
                                                <tr>
                                                      <td style={tdStyle}>{totals.completed}</td>
                                                      <td style={tdStyle}>{totals.rejections}</td>
                                                      <td style={tdStyle}>{totals.rate}</td>
                                                </tr>
                                          </tbody>
                                    </table>
                              </Panel>

                        </>}
            </div>

      )


}
/*
                                
*/
export default RejectionsReport;